import {
  BottomSheet,
  List,
  ListItem,
  ListItemText,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { HoldToConfirmButton } from "@src/appV2/redesign/components/HoldToConfirmButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { type PlacementDetail } from "../api/useGetPlacement";
import { PLACEMENT_PATHS } from "../paths";
import { shouldShowRequirement } from "../utils";

interface SubmitInterviewDocumentsLaterBottomSheetProps {
  modalState: UseModalState;
  placementDetails: PlacementDetail;
  onConfirm: () => void;
  onDismiss: () => void;
  isLoading?: boolean;
}

export function SubmitInterviewDocumentsLaterBottomSheet(
  props: SubmitInterviewDocumentsLaterBottomSheetProps
) {
  const { modalState, placementDetails, onConfirm, onDismiss, isLoading = false } = props;
  const history = useHistory();

  const missingWorkerRequirements =
    placementDetails.workerRequirements?.filter((requirement) =>
      shouldShowRequirement(requirement)
    ) ?? [];
  const missingCredentialRequirements =
    placementDetails.credentialRequirements?.filter((requirement) =>
      shouldShowRequirement(requirement)
    ) ?? [];
  const missingDocuments = [...missingWorkerRequirements, ...missingCredentialRequirements];

  const eventProperties = useMemo(
    () => ({
      placementId: placementDetails.id,
      workplaceId: placementDetails.workplace.id,
      missingDocumentsCount: missingDocuments.length,
    }),
    [placementDetails.id, placementDetails.workplace.id, missingDocuments.length]
  );

  // Track when the bottom sheet is viewed
  useLogEffect(APP_V2_APP_EVENTS.SUBMIT_DOCUMENTS_LATER_BOTTOM_SHEET_VIEWED, eventProperties);

  const handleHoldStart = useCallback(() => {
    logEvent(APP_V2_APP_EVENTS.SUBMIT_DOCUMENTS_LATER_HOLD_STARTED, eventProperties);
  }, [eventProperties]);

  const handleHoldComplete = useCallback(() => {
    logEvent(APP_V2_APP_EVENTS.SUBMIT_DOCUMENTS_LATER_HOLD_COMPLETED, eventProperties);
    onConfirm();
  }, [eventProperties, onConfirm]);

  const handleDismiss = useCallback(() => {
    logEvent(APP_V2_APP_EVENTS.SUBMIT_DOCUMENTS_LATER_DISMISSED, eventProperties);
    onDismiss();
  }, [eventProperties, onDismiss]);

  const handleDismissAndNavigate = useCallback(() => {
    handleDismiss();
    history.push(PLACEMENT_PATHS.ROOT);
  }, [handleDismiss, history]);

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={handleDismiss} />}
      footer={
        <DialogFooter orientation="vertical">
          <HoldToConfirmButton
            fullWidth
            isLoading={isLoading}
            onConfirmStart={handleHoldStart}
            onConfirm={handleHoldComplete}
          >
            I have these qualifications and I will upload them
          </HoldToConfirmButton>
          <TextButton size="large" sx={{ my: 3 }} onClick={handleDismissAndNavigate}>
            I don&apos;t have these requirements, I&apos;ll look at other jobs
          </TextButton>
        </DialogFooter>
      }
      onClose={handleDismiss}
    >
      <Box sx={{ px: 7, py: 6 }}>
        <Stack spacing={6}>
          <Text variant="h4" sx={{ fontWeight: 500 }}>
            Qualification verification required
          </Text>

          <Text color={(theme) => theme.palette.text.secondary}>
            You will need to submit the following documents by{" "}
            <Text semibold inline>
              4pm the day before your interview
            </Text>{" "}
            to verify your qualifications for this position. If you do not have these documents, or
            do not submit them in time,{" "}
            <Text semibold inline>
              your interview will be cancelled
            </Text>
            :
          </Text>

          <List sx={{ listStyleType: "disc", pl: 5, width: "100%" }}>
            {missingDocuments.map((requirement) => (
              <ListItem key={requirement.id} sx={{ display: "list-item", pb: 0, pt: 1 }}>
                <ListItemText>
                  <Text bold>{requirement.relationships?.requirement?.data?.attributes?.name}</Text>
                </ListItemText>
              </ListItem>
            ))}
          </List>
        </Stack>
      </Box>
    </BottomSheet>
  );
}
